'use client'

import React, { useEffect, useMemo } from 'react'

import { IAnnualProgram } from '@/_types'
import { buildItem, getAnnualProgramItemCategory2, pushEvent } from '@/_utils/analytics/datalayer'
import {
	useAPDetailGender,
	ZAPDetailHero,
	ZAPDetailHighlights,
	ZAPDetailProgramDetail,
	ZAPDetailProgramContent
} from '@/app/[locale]/annual-programs/_components/ap-detail'
import {
	HIGHLIGHT_35_AMBULANCES,
	HIGHLIGHT_HOLISTIC_APPROACH,
	HIGHLIGHT_AVAILABLE_APPOINTMENTS,
	HIGHLIGHT_FREE_PARKING,
	HIGHLIGHT_YEAR_ROUND_CARE
} from '@/app/[locale]/annual-programs/_constants'
import { useAPDetailProgram } from '@/app/[locale]/annual-programs/_hooks'

import AMBDescription from './a-mb-description'

/**
 * Props for the AMBContentWrapper component.
 */
interface IProps {
	/** Male version of the annual program */
	maleProgram: IAnnualProgram
	/** Female version of the annual program */
	femaleProgram: IAnnualProgram
}

/**
 * Main content wrapper for Medante Balance annual program page.
 * Manages program selection based on gender and renders all page sections.
 *
 * Sections included:
 * - Hero section with program introduction
 * - Program highlights
 * - Detailed program description
 * - Program details and benefits
 *
 * @param maleProgram - Annual program data for male version
 * @param femaleProgram - Annual program data for female version
 *
 * @example
 * ```typescript
 * <AMBContentWrapper
 *   maleProgram={maleProgram}
 *   femaleProgram={femaleProgram}
 * />
 * ```
 */
export default function AMBContentWrapper({ maleProgram, femaleProgram }: IProps) {
	const { gender } = useAPDetailGender()
	const currentProgram = useAPDetailProgram(maleProgram, femaleProgram)

	useEffect(() => {
		const item = buildItem(
			{
				id: currentProgram.id,
				name: currentProgram.name,
				type: 'annual-program',
				price: currentProgram.price,
				priceOld: currentProgram.priceOld
			},
			{ item_category2: getAnnualProgramItemCategory2({ primaryCategory: gender }) }
		)
		pushEvent('view_item', { currency: 'EUR', value: currentProgram.price, items: [item] })
	}, [currentProgram, gender])

	const highlights = useMemo(
		() => [
			HIGHLIGHT_35_AMBULANCES,
			HIGHLIGHT_HOLISTIC_APPROACH,
			HIGHLIGHT_AVAILABLE_APPOINTMENTS,
			HIGHLIGHT_FREE_PARKING,
			HIGHLIGHT_YEAR_ROUND_CARE
		],
		[]
	)

	return (
		<>
			<ZAPDetailHero
				programName='Medante Balance'
				description='<b>Starajte sa o svoje zdravie celoročne.</b> S ročným programom Medante Balance máte zvýhodnené ceny, nadštandardné služby a ďalšie benefity Polikliniky Medante.'
				femaleButtonText='Medante Balance pre ženu'
				maleButtonText='Medante Balance pre muža'
				image={{
					src: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/annual-programs/girl-balance-on-wood.webp`,
					alt: 'Žena a muž v prírode',
					height: 382,
					width: 625
				}}
			/>

			<ZAPDetailHighlights highlights={highlights} />

			<AMBDescription />

			<ZAPDetailProgramDetail
				primaryCategory={gender}
				annualProgram={currentProgram}
				orderButtonText='Objednať Medante Balance'
			>
				<ZAPDetailProgramContent program={currentProgram} />
			</ZAPDetailProgramDetail>
		</>
	)
}
